import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, Mic, PenIcon, Phone, PlusCircle, Shield, Text, Users } from 'lucide-react';
import { useContext, useMemo, useState, useRef } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslate } from 'sbx-crm-menu-ui';
import { Button } from 'src/components/ui/button';
import { RadioButtonComponent } from 'src/utils/UtilsComponent';
import { AiAgentV2Data } from '../../../../types/AiAgentV2';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { agentDefaultColor, AgentSetupContext } from '../AgentSetupComponent';
import SkillKeyValueConfigComponent from './agentSkillsComponent/SkillKeyValueConfigComponent';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import { AppAgentContext } from 'src/context/ContextProvider';
import RolesComponent from 'src/components/shared/RolesComponent';
import { grantPermission } from 'src/utils';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { useParams } from 'react-router-dom';
import { uploadFileCompressedService } from 'src/services/AssetService';
import iziToast from 'izitoast';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import Permission from 'src/components/shared/Permission';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import { logo_placeholder } from '../AgentLayoutComponentV2';

const classNameLabel = 'text-purple-700 font-semibold text-[14px]';
const classNameInput =
    '!border-purple-300 !focus:border-purple-500 !focus:ring !focus:ring-purple-200 !focus:ring-opacity-50';
const AgentIdentityComponent = () => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { control, register, setValue, getValues } = useContext(AgentSetupContext);
    const [validation, setValidation] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const inputLogoRef = useRef<HTMLInputElement | null>(null);
    const {
        state: {
            session: { permissions, ai_agent_config },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();

    const [activeSection, setActiveSection] = useState('chat');

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const whiteListIps = useFieldArray({
        name: 'privacy.white_list_ips',
        control,
    });

    const whiteListSource = useFieldArray({
        name: 'privacy.white_list_source',
        control,
    });

    const whiteListCountries = useFieldArray({
        name: 'privacy.white_list_countries',
        control,
    });

    const identityFields = useMemo((): GroupTypes<keyof AiAgentV2Data> => {
        return {
            chat: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.agent_name',
                        label: agent('agent_name'),
                        placeholder: agent('agent_name'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.suggested_message',
                        label: agent('suggested_message'),
                        placeholder: agent('suggested_message'),
                        type: 'select',
                        classNameLabel,
                        classNameInput,
                        isMulti: true,
                        options: [],
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            privacy_policy_url: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.privacy_policy_url',
                        label: agent('privacy_policy_url'),
                        placeholder: agent('privacy_policy_url'),
                        classNameLabel,
                        classNameInput,
                        type: 'url',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            chat2: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.circular_alignment',
                        label: agent('circular_alignment'),
                        placeholder: agent('circular_alignment'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.background_user_color',
                        label: agent('background_user_color'),
                        classNameLabel,
                        placeholder: '#FFFFFF',
                        classNameInput,
                        type: 'color',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.text_user_color',
                        label: agent('text_user_color'),
                        placeholder: agent('text_user_color'),
                        classNameLabel,
                        type: 'color',
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.text_agent_color',
                        label: agent('text_agent_color'),
                        placeholder: agent('text_agent_color'),
                        classNameLabel,
                        classNameInput,
                        type: 'color',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.background_agent_color',
                        label: agent('background_agent_color'),
                        placeholder: agent('background_agent_color'),
                        classNameLabel,
                        type: 'color',
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.button_color',
                        label: agent('button_color'),
                        type: 'color',
                        placeholder: agent('button_color'),
                        classNameInput,
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.header_bg_color',
                        label: agent('header_bg_color_label'),
                        type: 'color',
                        placeholder: agent('header_bg_color_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.header_text_color',
                        label: agent('header_text_color_label'),
                        type: 'color',
                        placeholder: agent('header_text_color_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.header_menu_color',
                        label: agent('header_menu_color_label'),
                        type: 'color',
                        placeholder: agent('header_menu_color_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            voice: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'look_and_feel_voice.name',
                        label: agent('agent_name'),
                        placeholder: agent('agent_name'),
                        classNameInput,
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            whatsapp: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'look_and_feel_whatsapp.name',
                        label: agent('agent_name'),
                        classNameInput,
                        placeholder: agent('agent_name'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            privacy: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'privacy.public',
                        label: agent('public'),

                        classNameLabel,
                        type: 'checkbox',
                        classNameInput: 'data-[state=checked]:!bg-purple-700 !border-purple-700',
                        classNameContent: 'flex items-center gap-2',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            text_format_text_bubble: {
                columns: 'grid-cols-1 mb-3',
                label: agent('text_bubble'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.text_bubble',
                        label: agent('text_bubble'),
                        placeholder: agent('text_bubble'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            max: 30,
                        },
                    },
                ],
            },
            text_format_header: {
                columns: 'grid-cols-2 mb-3',
                label: agent('header'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.logo_width',
                        label: agent('header_logo_width_label'),
                        type: 'number',
                        placeholder: agent('header_logo_width_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            text_format_form_session: {
                columns: 'grid-cols-1 mb-3',
                label: agent('form_session_label'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.welcome_title',
                        label: agent('form_session_welcome_title'),
                        type: 'textarea',
                        placeholder: agent('form_session_welcome_title'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            text_format_form_session2: {
                columns: 'grid-cols-2 mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.name.label',
                        label: agent('form_session_name_label'),
                        placeholder: agent('form_session_name_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.name.placeholder',
                        label: agent('form_session_name_placeholder'),
                        placeholder: agent('form_session_name_placeholder'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.phone.title',
                        label: agent('form_session_phone_title'),
                        placeholder: agent('form_session_phone_title'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.phone.placeholder',
                        label: agent('form_session_phone_placeholder'),
                        placeholder: agent('form_session_phone_placeholder'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.phone.label',
                        label: agent('form_session_phone_label'),
                        placeholder: agent('form_session_phone_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.email.label',
                        label: agent('form_session_email_label'),
                        placeholder: agent('form_session_email_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.email.placeholder',
                        label: agent('form_session_email_placeholder'),
                        placeholder: agent('form_session_email_placeholder'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.privacy_policy_message',
                        label: agent('form_session_privacy_policy_message'),
                        placeholder: agent('form_session_privacy_policy_message'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.form_session.init_chat_message',
                        label: agent('form_session_init_chat_message'),
                        placeholder: agent('form_session_init_chat_message'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            text_format_form_chat: {
                columns: 'grid-cols-1 mb-3',
                label: agent('form_chat'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.initial_message',
                        label: agent('initial_message'),
                        type: 'textarea',
                        placeholder: agent('initial_message'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            text_format_form_chat2: {
                columns: 'grid-cols-2 mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.loading_suggestions_message',
                        label: agent('loading_suggestions_message'),
                        placeholder: agent('loading_suggestions_message'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.comunication_with_assesor_title',
                        label: agent('comunication_with_assesor_title'),
                        placeholder: agent('comunication_with_assesor_title'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.minimized_chat_text',
                        label: agent('minimized_chat_text'),
                        placeholder: agent('minimized_chat_text'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.maximized_chat_text',
                        label: agent('maximized_chat_text'),
                        placeholder: agent('maximized_chat_text'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.finalized_chat_text',
                        label: agent('finalized_chat_text'),
                        placeholder: agent('finalized_chat_text'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.search_placeholder',
                        label: agent('search_placeholder'),
                        placeholder: agent('search_placeholder'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.role_title',
                        label: agent('role_title'),
                        placeholder: agent('role_title'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.role_message',
                        label: agent('role_message'),
                        placeholder: agent('role_message'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.loading_message_file',
                        label: agent('loading_message_file_label'),
                        placeholder: agent('loading_message_file_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.loaded_message_file',
                        label: agent('loaded_message_file_label'),
                        placeholder: agent('loaded_message_file_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.call_agent_message',
                        label: agent('call_agent_message_label'),
                        placeholder: agent('call_agent_message_label'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
        };
    }, []);

    const subTabs = useMemo(() => {
        return [
            { id: 'chat', icon: MessageSquare, label: agent('look_and_feel_chat'), color: 'from-pink-500 to-rose-500' },
            { id: 'voice', icon: Mic, label: agent('look_and_feel_voice'), color: 'from-orange-500 to-amber-500' },
            {
                id: 'whatsapp',
                icon: Phone,
                label: agent('look_and_feel_whatsapp'),
                color: 'from-green-500 to-emerald-500',
            },
            { id: 'privacy', icon: Shield, label: agent('privacy'), color: 'from-blue-500 to-cyan-500' },
            { id: 'text_format', icon: Text, label: agent('text_format'), color: 'from-purple-500 to-pink-500' },
            {
                id: 'roles',
                icon: Users,
                label: 'Roles',
                color: 'from-purple-500 to-pink-500',
            },
        ];
    }, []);

    const onChangeLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files = [e.target.files[0]];
            setIsLoading(true);
            const res = await uploadFileCompressedService(ai_agent_config.folder_key_avatars, files);

            if (res.success) {
                const name = res.items[0].name;
                setValue('avatar', `${ai_agent_config.path_avatars}/${name}`);
                iziToast.success({
                    message: common('image_uploaded'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            } else {
                iziToast.warning({
                    message: common('error_uploading_image'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            }
        }
    };

    const openAssetsModal = () => {
        dispatch(
            appActions.openModal({
                type: ModalType.ASSETS_AGENT,
                data: {
                    title: common('select_image'),
                    setData: (item) => setValue('look_and_feel_chat.extra_config.logo_header', item),
                    onClose: () => dispatch(appActions.closeModal(ModalType.ASSETS_AGENT)),
                },
            })
        );
    };

    const srcImage = useMemo(() => {
        const logo = getValues().look_and_feel_chat.extra_config?.logo_header;
        if (logo) {
            return logo;
        } else return logo_placeholder;
    }, [getValues().look_and_feel_chat.extra_config?.logo_header]);

    return (
        <div className="p-6">
            <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold text-purple-800">{agent('identity_settings')}</CardTitle>
                </CardHeader>

                <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full p-4">
                    <TabsList className="grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 rounded-xl bg-purple-100 mb-36 md:mb-12 xl:mb-16">
                        {subTabs.map((option, index) => (
                            <TabsTrigger
                                key={index}
                                value={option.id}
                                className={`
                        flex items-center justify-center py-2 px-1
                        rounded-lg transition-all duration-300
                        data-[state=active]:bg-gradient-to-br ${option.color}
                        data-[state=active]:text-white data-[state=active]:shadow-lg
                        hover:bg-white hover:bg-opacity-50
                      `}
                            >
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-center"
                                >
                                    <option.icon className="w-5 h-5 mr-2" />
                                    <span className="text-sm font-medium">{option.label}</span>
                                </motion.div>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeSection}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <TabsContent value="chat">
                                <div className={`flex flex-col`}>
                                    {identityFields['chat'].label && identityFields['chat'].label}
                                    <div
                                        className={`grid ${identityFields['chat'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['chat'].items}
                                            control={control}
                                        />
                                    </div>

                                    <div
                                        className={`grid ${identityFields['privacy_policy_url'].columns ?? 'grid-cols-1'} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['privacy_policy_url'].items}
                                            control={control}
                                        />
                                    </div>

                                    <div
                                        className={`grid ${identityFields['chat2'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['chat2'].items}
                                            control={control}
                                        />
                                    </div>

                                    <div className="flex justify-end">
                                        <ButtonComponent
                                            label={common('reset_styles')}
                                            className="primary-gradient-button"
                                            onClick={() => {
                                                setValue('look_and_feel_chat', {
                                                    ...getValues('look_and_feel_chat'),
                                                    ...agentDefaultColor,
                                                    extra_config: {
                                                        ...getValues('look_and_feel_chat.extra_config'),
                                                        header_bg_color: '',
                                                        header_menu_color: '#ffffff',
                                                        header_text_color: '#ffffff',
                                                    },
                                                });
                                            }}
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value="voice">
                                <div className={`flex flex-col`}>
                                    {identityFields['voice'].label && identityFields['voice'].label}
                                    <div
                                        className={`grid ${identityFields['voice'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['voice'].items}
                                            control={control}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'whatsapp'}>
                                <div className={`flex flex-col`}>
                                    {identityFields['whatsapp'].label && identityFields['whatsapp'].label}
                                    <div
                                        className={`grid ${identityFields['whatsapp'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['whatsapp'].items}
                                            control={control}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'privacy'}>
                                <div className={`flex flex-col`}>
                                    {identityFields['privacy'].label && identityFields['privacy'].label}
                                    <div
                                        className={`grid ${identityFields['privacy'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['privacy'].items}
                                            control={control}
                                        />

                                        <Card>
                                            <CardContent className="py-3">
                                                <SkillKeyValueConfigComponent
                                                    label={agent('white_list_ips')}
                                                    placeholderKey={'Ip'}
                                                    placeholderValue={common('request_per_minute')}
                                                    append={() => {
                                                        whiteListIps.append({
                                                            name: '',
                                                            value: '',
                                                        });
                                                    }}
                                                    name={'white_list_ips'}
                                                    buttonLabel={`${common('add')} ${agent('white_list_ips')}`}
                                                    formArray={whiteListIps}
                                                    register={register}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardContent className="py-3">
                                                <SkillKeyValueConfigComponent
                                                    label={agent('white_list_source')}
                                                    placeholderKey={common('source')}
                                                    placeholderValue={common('request_per_minute')}
                                                    append={() => {
                                                        whiteListSource.append({
                                                            name: '',
                                                            value: '',
                                                        });
                                                    }}
                                                    name={'white_list_source'}
                                                    buttonLabel={`${common('add')} ${agent('white_list_source')}`}
                                                    formArray={whiteListSource}
                                                    register={register}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardContent className="py-3">
                                                <SkillKeyValueConfigComponent
                                                    label={agent('white_list_countries')}
                                                    placeholderKey={common('country')}
                                                    placeholderValue={common('request_per_minute')}
                                                    append={() => {
                                                        whiteListCountries.append({
                                                            name: '',
                                                            value: '',
                                                        });
                                                    }}
                                                    name={'white_list_countries'}
                                                    buttonLabel={`${common('add')} ${agent('white_list_countries')}`}
                                                    formArray={whiteListCountries}
                                                    register={register}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>
                                        <label
                                            htmlFor=""
                                            className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-bold text-[20px]"
                                        >
                                            Autenticación
                                        </label>
                                        <Card>
                                            <CardContent className="flex items-center p-3">
                                                <RadioButtonComponent
                                                    classNameLabel="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[14px]"
                                                    label={'WhatsApp'}
                                                    name={'option-validation'}
                                                    value={'whatsapp'}
                                                    checked={validation === 'whatsapp'}
                                                    onChange={(value) => {
                                                        setValidation(value);
                                                        setValue('privacy.authentication_type', value);
                                                    }}
                                                    styleInput={{ accentColor: '#a855f7' }}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="flex items-center p-3">
                                                <RadioButtonComponent
                                                    classNameLabel="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[14px]"
                                                    label={'SMS'}
                                                    name={'option-validation'}
                                                    value={'sms'}
                                                    checked={validation === 'sms'}
                                                    onChange={(value) => {
                                                        setValidation(value);
                                                        setValue('privacy.authentication_type', value);
                                                    }}
                                                    styleInput={{ accentColor: '#a855f7' }}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="flex items-center p-3">
                                                <RadioButtonComponent
                                                    classNameLabel="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[14px]"
                                                    label={'Email'}
                                                    name={'option-validation'}
                                                    value={'mail'}
                                                    checked={validation === 'mail'}
                                                    onChange={(value) => {
                                                        setValidation(value);
                                                        setValue('privacy.authentication_type', value);
                                                    }}
                                                    styleInput={{ accentColor: '#a855f7' }}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="flex items-center gap-3 p-3">
                                                <RadioButtonComponent
                                                    classNameInput=""
                                                    classNameLabel="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[14px]"
                                                    label={'Document ID'}
                                                    name={'option-validation'}
                                                    value={'id'}
                                                    checked={validation === 'id'}
                                                    onChange={(value) => {
                                                        setValidation(value);
                                                        setValue('privacy.authentication_type', value);
                                                    }}
                                                    styleInput={{ accentColor: '#a855f7' }}
                                                    disabled={id !== 'create' && !allowEdit}
                                                />
                                                {validation === 'id' && (
                                                    <Button
                                                        type="button"
                                                        size={'sm'}
                                                        disabled={id !== 'create' && !allowEdit}
                                                        className="primary-gradient-button"
                                                        onClick={() => {
                                                            // dispatch(appActions.openModal({
                                                            //     type: ModalType.DYNAMIC,
                                                            //     data: {
                                                            //         title: 'Config',
                                                            //         state: [],
                                                            //         children: (data,) => {
                                                            //             return (
                                                            //                 <TableComponentV2
                                                            //                     items={data}
                                                            //                     header={[
                                                            //                         {
                                                            //                            key: 'name',
                                                            //                            label: 'Name',
                                                            //                            type: KeyHeader.KEY
                                                            //                         },
                                                            //                         {
                                                            //                            key: 'password',
                                                            //                            label: 'Password',
                                                            //                            type: KeyHeader.KEY
                                                            //                         }
                                                            //                     ]}
                                                            //                 />
                                                            //             )
                                                            //         },
                                                            //         onClose: () => dispatch(appActions.closeModal(ModalType.DYNAMIC))
                                                            //     }
                                                            // }));
                                                        }}
                                                    >
                                                        <PlusCircle className=" mr-1 h-4 w-4" />
                                                        Config
                                                    </Button>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'text_format'}>
                                <div className={`flex flex-col mt-28 min-[1025px]:mt-3`}>
                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                        {identityFields['text_format_text_bubble'].label &&
                                            identityFields['text_format_text_bubble'].label}
                                    </h3>
                                    <div
                                        className={`grid ${identityFields['text_format_text_bubble'].columns ?? 'grid-cols-1 '} gap-3 mb-5`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_text_bubble'].items}
                                            control={control}
                                        />
                                    </div>
                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                        {identityFields['text_format_header'].label}
                                    </h3>
                                    <div
                                        className={`grid ${identityFields['text_format_header'].columns ?? 'grid-cols-1 '} gap-3 mb-5`}
                                    >
                                        <div className="flex gap-2 items-center">
                                            <label
                                                htmlFor=""
                                                className="block text-sm  leading-6 !text-purple-700 !font-semibold"
                                            >
                                                {agent('logo_header_label')}
                                            </label>
                                            <div className="py-2 px-4 flex gap-2">
                                                <input
                                                    className="hidden"
                                                    type="file"
                                                    accept="image/png, image/jpeg"
                                                    onChange={onChangeLogo}
                                                    ref={inputLogoRef}
                                                />
                                                <div className="relative">
                                                    <img
                                                        src={srcImage}
                                                        alt="avatar"
                                                        width={45}
                                                        className="rounded-full"
                                                    />
                                                    <Permission permission={AI_AGENT_V2_EDIT}>
                                                        <div
                                                            className="bg-white rounded-full cursor-pointer bottom-[-6px] right-0 w-6 flex justify-center items-center shadow absolute"
                                                            onClick={openAssetsModal}
                                                        >
                                                            <PenIcon width={12} />
                                                        </div>
                                                    </Permission>
                                                </div>
                                                {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                                            </div>
                                        </div>
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_header'].items}
                                            control={control}
                                        />
                                    </div>
                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                        {identityFields['text_format_form_session'].label &&
                                            identityFields['text_format_form_session'].label}
                                    </h3>
                                    <div
                                        className={`grid ${identityFields['text_format_form_session'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_form_session'].items}
                                            control={control}
                                        />
                                    </div>
                                    <div
                                        className={`grid ${identityFields['text_format_form_session2'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_form_session2'].items}
                                            control={control}
                                        />
                                    </div>

                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mt-3 mb-2">
                                        {identityFields['text_format_form_chat'].label &&
                                            identityFields['text_format_form_chat'].label}
                                    </h3>

                                    <div
                                        className={`grid ${identityFields['text_format_form_chat'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_form_chat'].items}
                                            control={control}
                                        />
                                    </div>

                                    <div
                                        className={`grid ${identityFields['text_format_form_chat2'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_form_chat2'].items}
                                            control={control}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'roles'}>
                                <RolesComponent
                                    value={getValues().look_and_feel_chat?.extra_config?.roles_options || []}
                                    setValue={(val) => {
                                        setValue('look_and_feel_chat.extra_config.roles_options', val);
                                    }}
                                    disabled={id !== 'create' && !allowEdit}
                                />
                            </TabsContent>
                        </motion.div>
                    </AnimatePresence>
                </Tabs>
            </Card>
        </div>
    );
};

export default AgentIdentityComponent;
